/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type KeyboardEvent, type ReactNode } from 'react';

import { I18n } from '@coze-arch/i18n';
import {
  IconCozApply,
  IconCozArrowRight,
  IconCozBot,
  IconCozCross,
} from '@coze-arch/coze-design/icons';
import { Badge, Modal, type ModalProps } from '@coze-arch/coze-design';

import { useHiddenSession } from '../../hooks/use-hidden-session';

import styles from './index.module.less';

export type CreateType = 'project' | 'agent';

export interface GuideModalProps
  extends Omit<ModalProps, 'size' | 'footer' | 'header' | 'onCancel'> {
  onCancel: () => void;
  onChange: (type: CreateType) => void;
  extraButtonConfigs?: GuideButtonProps[];
}

interface GuideButtonProps {
  onClick: () => void;
  title: ReactNode;
  description: ReactNode;
  tip?: ReactNode;
  icon?: ReactNode;
}

const handleOptionKeyDown = (
  event: KeyboardEvent<HTMLDivElement>,
  onClick: () => void,
) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onClick();
  }
};

export const GuideButton: React.FC<GuideButtonProps> = ({
  onClick,
  title,
  description,
  tip,
  icon,
}) => {
  const { isSessionHidden, hideSession } = useHiddenSession(
    tip ? 'guideTip' : '',
  );
  const showTip = !isSessionHidden && Boolean(tip);

  return (
    <>
      {showTip ? (
        <div className={styles['guide-tip']}>
          <span>{tip}</span>
          <IconCozCross
            className="w-[12px] h-[12px] shrink-0 cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              hideSession();
            }}
          />
        </div>
      ) : null}
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={event => handleOptionKeyDown(event, onClick)}
        className={styles['guide-option']}
      >
        <div className={styles['guide-option-icon']}>{icon}</div>
        <div className={styles['guide-option-body']}>
          <div className={styles['guide-option-title']}>{title}</div>
          <div className={styles['guide-option-desc']}>{description}</div>
        </div>
        <IconCozArrowRight className={styles['guide-option-arrow']} />
      </div>
    </>
  );
};

export const GuideModal: React.FC<GuideModalProps> = ({
  onChange,
  extraButtonConfigs = [],
  ...modalProps
}) => (
  <Modal
    className={styles['guide-modal']}
    size="medium"
    title={I18n.t('create_title')}
    width={400}
    {...modalProps}
  >
    <div className={styles['guide-option-list']}>
      <GuideButton
        onClick={() => onChange('agent')}
        icon={<IconCozBot />}
        title={I18n.t('creat_project_creat_agent')}
        description={I18n.t('creat_project_agent_describe')}
        tip={!IS_OPEN_SOURCE ? I18n.t('agent_creat_tips') : null}
      />
      <GuideButton
        onClick={() => onChange('project')}
        icon={<IconCozApply />}
        title={
          <span className={styles['guide-option-title-row']}>
            {I18n.t('creat_project_creat_project')}
            <Badge count="Beta" type="alt" />
          </span>
        }
        description={
          IS_OPEN_SOURCE
            ? I18n.t('creat_project_describe_open')
            : I18n.t('creat_project_describe')
        }
      />
      {extraButtonConfigs.map(({ onClick, ...config }, index) => (
        <GuideButton
          key={index}
          onClick={() => {
            modalProps.onCancel();
            onClick();
          }}
          {...config}
        />
      ))}
    </div>
  </Modal>
);

GuideModal.displayName = 'GuideModal';
